// src/components/Settings/SettingsForm.tsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  TextField,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  Paper,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  useTheme,
  InputAdornment,
  Badge,
  Avatar,
} from "@mui/material";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Link as LinkIcon,
  Palette as PaletteIcon,
  Image as ImageIcon,
  Phone as PhoneIcon,
  Description as DescriptionIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
} from "@mui/icons-material";
import { HexColorPicker } from "react-colorful";
import {
  fetchSettings,
  updateSettings,
  Settings,
  getImageUrl,
} from "../../services/api";
import PageHeader from "../common/PageHeader";
import {
  mockFetchSettings,
  mockUpdateSettings,
} from "../../utils/mockSettingsData";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const SettingsForm: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  const [formData, setFormData] = useState<Settings>({
    id: 0,
    radioUrl: "",
    about: "",
    siteUrl: "",
    phoneNumber: "",
    baseUrl: "",
    faceBookUrl: "",
    instagramUrl: "",
    youtubeUrl: "",
    appIconPath: "",
    sideMenuBackgroundPath: "",
    dashboardBackgroundPath: "",
    aboutUsBackgroundPath: "",
    primaryColor: "#000000",
    secondaryColor: "#ffffff",
    textColorOnPrimary: "#ffffff",
    textColorOnSecondary: "#000000",
  });

  const [files, setFiles] = useState<{
    appIcon: File | null;
    sideMenuBackground: File | null;
    dashboardBackground: File | null;
    aboutUsBackground: File | null;
  }>({
    appIcon: null,
    sideMenuBackground: null,
    dashboardBackground: null,
    aboutUsBackground: null,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const { data: settingsData, isLoading: isSettingsLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: fetchSettings,
  });

  // Example of how to use mock data in useEffect
  useEffect(() => {
    // Check if we should use mock data (e.g., in development/testing)
    const useMockData = false;
    // process.env.REACT_APP_USE_MOCK_DATA === "true" ||
    // process.env.NODE_ENV === "development";

    if (useMockData) {
      // Simulate API call with mock data
      const loadMockData = async () => {
        try {
          // Set loading state if needed
          // setIsLoading(true);

          // Simulate API call with 1 second delay
          const mockData = await mockFetchSettings(1000, true, false);

          // Update your form data state with the mock data
          setFormData(mockData);

          // Optional: Log for debugging
          console.log("Loaded mock settings data:", mockData);
        } catch (error) {
          console.error("Error loading mock data:", error);
          // Handle error state if needed
        } finally {
          // Set loading state back if needed
          // setIsLoading(false);
        }
      };

      loadMockData();
    }
    // If not using mock data, rely on your regular useQuery hook
  }, []);

  useEffect(() => {
    if (settingsData) {
      setFormData(settingsData);
    }
  }, [settingsData]);

  const mutation = useMutation<Settings, Error, FormData>({
    mutationFn: updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      setSnackbar({
        open: true,
        message: "Settings updated successfully",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error: ${error.message}`,
        severity: "error",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formDataToSend = new FormData();

    // Append text fields
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== null && value !== undefined && key !== "id") {
        formDataToSend.append(key, value.toString());
      }
    });

    // Handle file fields
    const fileFields = [
      "appIcon",
      "sideMenuBackground",
      "dashboardBackground",
      "aboutUsBackground",
    ] as const;
    fileFields.forEach((field) => {
      if (files[field]) {
        formDataToSend.set(field, files[field]!);
      }
    });

    // Perform the mutation
    mutation.mutate(formDataToSend);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setFiles((prev) => ({
        ...prev,
        [name]: files[0],
      }));
    }
  };

  const handleDeleteImage = (field: keyof typeof files) => {
    setFiles((prev) => ({
      ...prev,
      [field]: null,
    }));
    setFormData((prev) => ({
      ...prev,
      [`${field}Path`]: "",
    }));
  };

  const handleColorChange = (name: string) => (color: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: color,
    }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (isSettingsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Application Settings"
        breadcrumbs={[{ label: "Dashboard", path: "/" }, { label: "Settings" }]}
        action={
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                <span>Saving...</span>
              </Box>
            ) : (
              "Save Changes"
            )}
          </Button>
        }
      />

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          An error occurred while saving settings. Please try again.
        </Alert>
      )}

      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="settings tabs"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            label="General"
            icon={<InfoIcon />}
            iconPosition="start"
            sx={{ minHeight: 64 }}
          />
          <Tab
            label="Appearance"
            icon={<PaletteIcon />}
            iconPosition="start"
            sx={{ minHeight: 64 }}
          />
          <Tab
            label="Media"
            icon={<ImageIcon />}
            iconPosition="start"
            sx={{ minHeight: 64 }}
          />
          <Tab
            label="Social Media"
            icon={<LinkIcon />}
            iconPosition="start"
            sx={{ minHeight: 64 }}
          />
        </Tabs>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          {/* General Settings */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  id="siteUrl"
                  label="Website URL"
                  name="siteUrl"
                  value={formData.siteUrl}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  id="radioUrl"
                  label="Radio Stream URL"
                  name="radioUrl"
                  value={formData.radioUrl}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  id="baseUrl"
                  label="API Base URL"
                  name="baseUrl"
                  value={formData.baseUrl}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="phoneNumber"
                  label="Contact Phone Number"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="about"
                  label="About"
                  name="about"
                  multiline
                  rows={6}
                  value={formData.about}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DescriptionIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Appearance Settings */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Color Scheme
            </Typography>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6} lg={3}>
                <Card sx={{ height: "100%" }}>
                  <CardHeader
                    title="Primary Color"
                    titleTypographyProps={{ variant: "subtitle1" }}
                  />
                  <Divider />
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <HexColorPicker
                        color={formData.primaryColor}
                        onChange={handleColorChange("primaryColor")}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        value={formData.primaryColor}
                        onChange={(e) =>
                          handleColorChange("primaryColor")(e.target.value)
                        }
                        sx={{ mt: 1 }}
                      />
                      <Box
                        sx={{
                          width: "100%",
                          height: 40,
                          bgcolor: formData.primaryColor,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Card sx={{ height: "100%" }}>
                  <CardHeader
                    title="Secondary Color"
                    titleTypographyProps={{ variant: "subtitle1" }}
                  />
                  <Divider />
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <HexColorPicker
                        color={formData.secondaryColor}
                        onChange={handleColorChange("secondaryColor")}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        value={formData.secondaryColor}
                        onChange={(e) =>
                          handleColorChange("secondaryColor")(e.target.value)
                        }
                        sx={{ mt: 1 }}
                      />
                      <Box
                        sx={{
                          width: "100%",
                          height: 40,
                          bgcolor: formData.secondaryColor,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Card sx={{ height: "100%" }}>
                  <CardHeader
                    title="Text on Primary"
                    titleTypographyProps={{ variant: "subtitle1" }}
                  />
                  <Divider />
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <HexColorPicker
                        color={formData.textColorOnPrimary}
                        onChange={handleColorChange("textColorOnPrimary")}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        value={formData.textColorOnPrimary}
                        onChange={(e) =>
                          handleColorChange("textColorOnPrimary")(
                            e.target.value
                          )
                        }
                        sx={{ mt: 1 }}
                      />
                      <Box
                        sx={{
                          width: "100%",
                          height: 40,
                          bgcolor: formData.primaryColor,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          color: formData.textColorOnPrimary,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography>Sample Text</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Card sx={{ height: "100%" }}>
                  <CardHeader
                    title="Text on Secondary"
                    titleTypographyProps={{ variant: "subtitle1" }}
                  />
                  <Divider />
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <HexColorPicker
                        color={formData.textColorOnSecondary}
                        onChange={handleColorChange("textColorOnSecondary")}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        value={formData.textColorOnSecondary}
                        onChange={(e) =>
                          handleColorChange("textColorOnSecondary")(
                            e.target.value
                          )
                        }
                        sx={{ mt: 1 }}
                      />
                      <Box
                        sx={{
                          width: "100%",
                          height: 40,
                          bgcolor: formData.secondaryColor,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          color: formData.textColorOnSecondary,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography>Sample Text</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Preview
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 2,
                    p: 2,
                    borderRadius: 1,
                    bgcolor: formData.primaryColor,
                    color: formData.textColorOnPrimary,
                  }}
                >
                  <Typography variant="h6">Primary Color Header</Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    borderRadius: 1,
                    bgcolor: formData.secondaryColor,
                    color: formData.textColorOnSecondary,
                  }}
                >
                  <Typography variant="body1">
                    Secondary Color Content Area
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </TabPanel>

          {/* Media Settings */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={3}>
                <Card>
                  <CardHeader
                    title="App Icon"
                    titleTypographyProps={{ variant: "subtitle1" }}
                    action={
                      <Tooltip title="Recommended size: 512x512 pixels">
                        <IconButton size="small">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                  />
                  <Divider />
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        p: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: 150,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          bgcolor: "background.default",
                          borderRadius: 2,
                          border: "1px dashed",
                          borderColor: "divider",
                          mb: 2,
                          position: "relative",
                        }}
                      >
                        {formData.appIconPath ? (
                          <>
                            <Box
                              component="img"
                              src={getImageUrl(formData.appIconPath)}
                              alt="App Icon"
                              sx={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                borderRadius: 2,
                              }}
                            />
                            <IconButton
                              size="small"
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                bgcolor: "background.paper",
                              }}
                              onClick={() => handleDeleteImage("appIcon")}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <Typography color="text.secondary">
                            No image uploaded
                          </Typography>
                        )}
                      </Box>

                      <input
                        accept="image/*"
                        style={{ display: "none" }}
                        id="appIcon"
                        type="file"
                        onChange={handleFileChange}
                        name="appIcon"
                      />
                      <label htmlFor="appIcon">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadIcon />}
                          sx={{ width: "100%" }}
                        >
                          {formData.appIconPath
                            ? "Replace Icon"
                            : "Upload Icon"}
                        </Button>
                      </label>
                      {files.appIcon && (
                        <Typography variant="body2" color="text.secondary">
                          Selected: {files.appIcon.name}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Card>
                  <CardHeader
                    title="Side Menu Background"
                    titleTypographyProps={{ variant: "subtitle1" }}
                    action={
                      <Tooltip title="Recommended size: 1080x1920 pixels">
                        <IconButton size="small">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                  />
                  <Divider />
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        p: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: 150,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          bgcolor: "background.default",
                          borderRadius: 2,
                          border: "1px dashed",
                          borderColor: "divider",
                          mb: 2,
                          position: "relative",
                        }}
                      >
                        {formData.sideMenuBackgroundPath ? (
                          <>
                            <Box
                              component="img"
                              src={getImageUrl(formData.sideMenuBackgroundPath)}
                              alt="Side Menu Background"
                              sx={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                borderRadius: 2,
                              }}
                            />
                            <IconButton
                              size="small"
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                bgcolor: "background.paper",
                              }}
                              onClick={() =>
                                handleDeleteImage("sideMenuBackground")
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <Typography color="text.secondary">
                            No image uploaded
                          </Typography>
                        )}
                      </Box>

                      <input
                        accept="image/*"
                        style={{ display: "none" }}
                        id="sideMenuBackground"
                        type="file"
                        onChange={handleFileChange}
                        name="sideMenuBackground"
                      />
                      <label htmlFor="sideMenuBackground">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadIcon />}
                          sx={{ width: "100%" }}
                        >
                          {formData.sideMenuBackgroundPath
                            ? "Replace Image"
                            : "Upload Image"}
                        </Button>
                      </label>
                      {files.sideMenuBackground && (
                        <Typography variant="body2" color="text.secondary">
                          Selected: {files.sideMenuBackground.name}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Card>
                  <CardHeader
                    title="Dashboard Background"
                    titleTypographyProps={{ variant: "subtitle1" }}
                    action={
                      <Tooltip title="Recommended size: 1920x1080 pixels">
                        <IconButton size="small">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                  />
                  <Divider />
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        p: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: 150,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          bgcolor: "background.default",
                          borderRadius: 2,
                          border: "1px dashed",
                          borderColor: "divider",
                          mb: 2,
                          position: "relative",
                        }}
                      >
                        {formData.dashboardBackgroundPath ? (
                          <>
                            <Box
                              component="img"
                              src={getImageUrl(
                                formData.dashboardBackgroundPath
                              )}
                              alt="Dashboard Background"
                              sx={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                borderRadius: 2,
                              }}
                            />
                            <IconButton
                              size="small"
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                bgcolor: "background.paper",
                              }}
                              onClick={() =>
                                handleDeleteImage("dashboardBackground")
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <Typography color="text.secondary">
                            No image uploaded
                          </Typography>
                        )}
                      </Box>

                      <input
                        accept="image/*"
                        style={{ display: "none" }}
                        id="dashboardBackground"
                        type="file"
                        onChange={handleFileChange}
                        name="dashboardBackground"
                      />
                      <label htmlFor="dashboardBackground">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadIcon />}
                          sx={{ width: "100%" }}
                        >
                          {formData.dashboardBackgroundPath
                            ? "Replace Image"
                            : "Upload Image"}
                        </Button>
                      </label>
                      {files.dashboardBackground && (
                        <Typography variant="body2" color="text.secondary">
                          Selected: {files.dashboardBackground.name}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Card>
                  <CardHeader
                    title="About Us Background"
                    titleTypographyProps={{ variant: "subtitle1" }}
                    action={
                      <Tooltip title="Recommended size: 1920x1080 pixels">
                        <IconButton size="small">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                  />
                  <Divider />
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        p: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: 150,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          bgcolor: "background.default",
                          borderRadius: 2,
                          border: "1px dashed",
                          borderColor: "divider",
                          mb: 2,
                          position: "relative",
                        }}
                      >
                        {formData.aboutUsBackgroundPath ? (
                          <>
                            <Box
                              component="img"
                              src={getImageUrl(formData.aboutUsBackgroundPath)}
                              alt="About Us Background"
                              sx={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                borderRadius: 2,
                              }}
                            />
                            <IconButton
                              size="small"
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                bgcolor: "background.paper",
                              }}
                              onClick={() =>
                                handleDeleteImage("aboutUsBackground")
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <Typography color="text.secondary">
                            No image uploaded
                          </Typography>
                        )}
                      </Box>

                      <input
                        accept="image/*"
                        style={{ display: "none" }}
                        id="aboutUsBackground"
                        type="file"
                        onChange={handleFileChange}
                        name="aboutUsBackground"
                      />
                      <label htmlFor="aboutUsBackground">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<UploadIcon />}
                          sx={{ width: "100%" }}
                        >
                          {formData.aboutUsBackgroundPath
                            ? "Replace Image"
                            : "Upload Image"}
                        </Button>
                      </label>
                      {files.aboutUsBackground && (
                        <Typography variant="body2" color="text.secondary">
                          Selected: {files.aboutUsBackground.name}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Social Media Settings */}
          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="faceBookUrl"
                  label="Facebook URL"
                  name="faceBookUrl"
                  value={formData.faceBookUrl}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FacebookIcon />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="https://facebook.com/yourpage"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="instagramUrl"
                  label="Instagram URL"
                  name="instagramUrl"
                  value={formData.instagramUrl}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <InstagramIcon />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="https://instagram.com/yourhandle"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="youtubeUrl"
                  label="YouTube URL"
                  name="youtubeUrl"
                  value={formData.youtubeUrl}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <YouTubeIcon />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="https://youtube.com/c/yourchannel"
                />
              </Grid>

              <Grid item xs={12}>
                <Card sx={{ mt: 3 }}>
                  <CardHeader
                    title="Social Media Preview"
                    titleTypographyProps={{ variant: "h6" }}
                  />
                  <Divider />
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        gap: 3,
                      }}
                    >
                      {formData.faceBookUrl && (
                        <Paper
                          elevation={1}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            minWidth: 200,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            sx={{ bgcolor: "#4267B2", width: 48, height: 48 }}
                          >
                            <FacebookIcon fontSize="large" />
                          </Avatar>
                          <Typography variant="subtitle2">Facebook</Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            component="a"
                            href={formData.faceBookUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Visit Page
                          </Button>
                        </Paper>
                      )}

                      {formData.instagramUrl && (
                        <Paper
                          elevation={1}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            minWidth: 200,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            sx={{
                              backgroundImage:
                                "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
                              width: 48,
                              height: 48,
                            }}
                          >
                            <InstagramIcon fontSize="large" />
                          </Avatar>
                          <Typography variant="subtitle2">Instagram</Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            component="a"
                            href={formData.instagramUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Visit Profile
                          </Button>
                        </Paper>
                      )}

                      {formData.youtubeUrl && (
                        <Paper
                          elevation={1}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            minWidth: 200,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            sx={{ bgcolor: "#FF0000", width: 48, height: 48 }}
                          >
                            <YouTubeIcon fontSize="large" />
                          </Avatar>
                          <Typography variant="subtitle2">YouTube</Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            component="a"
                            href={formData.youtubeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Visit Channel
                          </Button>
                        </Paper>
                      )}

                      {!formData.faceBookUrl &&
                        !formData.instagramUrl &&
                        !formData.youtubeUrl && (
                          <Box
                            sx={{
                              p: 3,
                              textAlign: "center",
                              width: "100%",
                              color: "text.secondary",
                            }}
                          >
                            <Typography>
                              No social media URLs configured
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Add your social media URLs above to see a preview
                            </Typography>
                          </Box>
                        )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Box>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={() => navigate("/")}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} color="inherit" />
              <span>Saving...</span>
            </Box>
          ) : (
            "Save Changes"
          )}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
export default SettingsForm;
